import { supabase } from "@/lib/supabase";
import { fetchCostSheetsByIds } from './manufacturing';

async function runQuery(query, fallback = []) {
  try {
    const { data, error } = await query;
    if (error) throw error;
    return { data: data ?? fallback, error: null };
  } catch (error) {
    console.error("Mfg production orders API error:", error);
    return { data: fallback, error };
  }
}

const ORDER_SELECT = `
  id, number, order_date, description, cod_fam, is_sample,
  family:familia!mfg_production_orders_cod_fam_fkey(cod_fam, descrip),
  temporada,
  season:mfc_temporada!mfg_production_orders_temporada_fkey(cod_tem, descrip),
  delivery_date, observations, source_type, source_simulation_id,
  total_quantity, process_total, material_total, grand_total,
  created_at, updated_at
`;

const ORDER_DETAIL_SELECT = `
  ${ORDER_SELECT},
  source_simulation:mfg_simulation_runs(id, description, simulation_date),
  products:mfg_production_order_products(
    *,
    product:mfg_products(id, code, description, images, main_image_index, temporada, composicion, cod_fam, family:familia!mfg_products_cod_fam_fkey(cod_fam, descrip)),
    color_lines:mfg_production_order_color_lines(*),
    cost_lines:mfg_production_order_cost_lines(*, process:mfg_manufacturing_processes(id, is_overhead, excludes_manufacturing, albaran_cutting_layout))
  )
`;

export const PRODUCTION_ORDER_STATUS_LABELS = {
  active: "Activa",
  closed: "Cerrada",
};

export const PRODUCTION_ORDER_SOURCE_LABELS = {
  manual: "Manual",
  simulation: "Simulación",
  customer_order: "Pedido",
};

export async function getNextProductionOrderNumber() {
  try {
    const { data, error } = await supabase.rpc("mfg_next_production_order_number");
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Mfg next production order number error:", error);
    return { data: null, error };
  }
}

export async function fetchProductionOrderTree({
  searchTerm = "", codFam = "", temporada = "", status = "", sourceType = "",
  limit = 101, offset = 0, pendingOnly = false,
} = {}) {
  return runQuery(supabase.rpc("mfg_production_order_tree_page", {
    p_search: searchTerm.trim(), p_cod_fam: codFam, p_temporada: temporada,
    p_pending_only: pendingOnly, p_status: status, p_source_type: sourceType, p_limit: limit, p_offset: offset,
  }));
}

export async function fetchProductionOrder(orderId) {
  if (!orderId) return { data: null, error: null };
  const result = await runQuery(
    supabase
      .from("mfg_production_orders")
      .select(ORDER_DETAIL_SELECT)
      .eq("id", orderId)
      .maybeSingle(),
    null
  );
  if (result.error || !result.data) return result;
  const sheets = await fetchCostSheetsByIds((result.data.products || []).map(product => product.cost_sheet_id));
  if (sheets.error) return { data: null, error: sheets.error };
  return { ...result, data: { ...result.data, products: (result.data.products || []).map(product => ({
    ...product, color_sheet: sheets.data.get(product.cost_sheet_id) || null,
  })) } };
}

function serializeOrderProducts(items = []) {
  return items
    .filter((item) => Number(item.total_quantity || 0) > 0)
    .map((item) => ({
      id: item.id || null,
      product_id: item.product_id,
      ...(item.updated_at ? { expected_updated_at: item.updated_at } : {}),
      // Omitted values let SQL preserve existing dates or initialize a new S/O.
      ...(item.order_date !== undefined ? { order_date: item.order_date } : {}),
      ...(item.finished_date !== undefined ? { finished_date: item.finished_date || null } : {}),
      cost_sheet_id: item.cost_sheet_id || null,
      product_code_snapshot: item.product_code_snapshot,
      description_snapshot: item.description_snapshot,
      manufacturer_reference: item.manufacturer_reference || null,
      pattern_code: item.pattern_code || null,
      season_snapshot: item.season_snapshot || null,
      notes: item.notes || null,
      unit_process_cost: Number(item.unit_process_cost || 0),
      unit_material_cost: Number(item.unit_material_cost || 0),
      unit_total_cost: Number(item.unit_total_cost || 0),
      total_quantity: Number(item.total_quantity || 0),
      total_process_cost: Number(item.total_process_cost || 0),
      total_material_cost: Number(item.total_material_cost || 0),
      total_cost: Number(item.total_cost || 0),
      color_lines: (item.color_lines || []).map((line) => ({
        color: line.color,
        variant_id: line.variant_id || null,
        garment_color: line.garment_color || line.color,
        material_colors: line.material_colors || {},
        contrast_1: line.contrast_1 || null,
        contrast_2: line.contrast_2 || null,
        contrast_3: line.contrast_3 || null,
        quantity_p: Number(line.quantity_p || 0),
        quantity_m: Number(line.quantity_m || 0),
        quantity_l: Number(line.quantity_l || 0),
        quantity_xl: Number(line.quantity_xl || 0),
        quantity_2xl: Number(line.quantity_2xl || 0),
        quantity_3xl: Number(line.quantity_3xl || 0),
        quantity_4xl: Number(line.quantity_4xl || 0),
        quantity_5xl: Number(line.quantity_5xl || 0),
        sort_order: Number(line.sort_order || 0),
      })),
      cost_lines: (item.cost_lines || []).map((line) => ({
        id: line.id || null,
        ...Object.fromEntries(['delivered_date', 'received_date', 'delivered_quantity', 'received_quantity', 'received_matrix']
          .filter((key) => line[key] !== undefined).map((key) => [key, line[key] === '' ? null : line[key]])),
        process_id: line.process_id || null,
        supplier_id: line.supplier_id || null,
        excludes_manufacturing: line.excludes_manufacturing,
        line_type: line.line_type,
        source_line_id: line.source_line_id || null,
        name_snapshot: line.name_snapshot,
        unit: line.unit || null,
        quantity_per_unit: Number(line.quantity_per_unit || 0),
        waste_pct: Number(line.waste_pct || 0),
        unit_cost: Number(line.unit_cost || 0),
        total_cost: Number(line.total_cost || 0),
        process_source_line_id: line.process_source_line_id || null,
        process_name_snapshot: line.process_name_snapshot || null,
        supplier_name_snapshot: line.supplier_name_snapshot || null,
        sort_order: Number(line.sort_order || 0),
      })),
    }));
}

function commonSeason(items = []) {
  const seasons = new Set(items.map((item) => item.season_snapshot).filter(Boolean));
  return seasons.size === 1 ? [...seasons][0] : null;
}

export async function saveProductionOrder({
  isSample = null,
  id = null,
  number = null,
  orderDate,
  description = "",
  temporada = null,
  deliveryDate = null,
  observations = "",
  sourceType = "manual",
  sourceSimulationId = null,
  items = [],
} = {}) {
  try {
    const products = serializeOrderProducts(items);
    if (!products.length) throw new Error("La orden de fabricación no contiene artículos");

    const params = {
      p_is_sample: isSample,
      p_order_id: id || null,
      p_number: number || null,
      p_order_date: orderDate || null,
      p_description: description?.trim() || null,
      p_cod_fam: null,
      p_temporada: temporada || commonSeason(products) || null,
      p_delivery_date: deliveryDate || null,
      p_observations: observations?.trim() || null,
      p_source_type: sourceType || "manual",
      p_source_simulation_id: sourceSimulationId || null,
      p_products: products,
    };

    const { data, error } = await supabase.rpc("save_mfg_production_order_with_type", params);
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Save mfg production order error:", error);
    return { data: null, error };
  }
}

export async function deleteProductionOrder(orderId) {
  if (!orderId) return { data: null, error: new Error("Orden de fabricación no válida") };
  return runQuery(supabase.rpc("delete_mfg_production_order_cascade", { p_order_id: orderId }), null);
}

export async function convertSimulationToProductionOrder(simulationId, { orderDate, deliveryDate = null, observations = "", expectedVersion = null } = {}) {
  try {
    if (!simulationId) throw new Error("Simulación no válida");
    const { data, error } = await supabase.rpc("convert_simulation_to_mfg_production_order", {
      p_simulation_id: simulationId,
      p_expected_version: expectedVersion,
      p_order_date: orderDate || null,
      p_delivery_date: deliveryDate || null,
      p_observations: observations?.trim() || null,
    });
    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error("Convert simulation to production order error:", error);
    return { data: null, error };
  }
}

export async function reloadSuborderCostSheet(suborderId, expectedPreview = null) {
  return runQuery(supabase.rpc("mfg_reload_suborder_cost_sheet", {
    p_suborder: suborderId, p_expected_preview: expectedPreview,
  }), null);
}

export const fetchPendingProductionDocuments = () => runQuery(supabase.rpc("mfg_supplier_return_pending_summary"));
